import { useCallback, useEffect, useRef, useState } from "react";
import { router, useForm } from "@inertiajs/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Bot,
  ListChecks,
  LoaderCircle,
  RotateCcw,
  Send,
  UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  WhatsAppListPicker,
  type WhatsAppInteractiveOption,
  type WhatsAppListInteractive,
} from "@/components/WhatsAppListPicker";
import { cn } from "@/lib/utils";

export type ChatbotPreviewInteractive =
  | WhatsAppListInteractive
  | {
      type: "button";
      body: string;
      options: WhatsAppInteractiveOption[];
    };

export interface ChatbotPreviewMessage {
  id: number;
  direction: "inbound" | "outbound";
  message: string;
  display_message: string;
  occurred_at: string;
  interactive?: ChatbotPreviewInteractive | null;
}

export interface ChatbotPreviewData {
  messages: ChatbotPreviewMessage[];
  limits: {
    message_length: number;
    stored_messages: number;
  };
}

interface ChatbotPreviewPanelProps extends ChatbotPreviewData {
  business: { id: number; name: string } | null;
  messagePath: string;
  resetPath: string;
  surface?: "onboarding";
  title?: string;
  compact?: boolean;
}

function AssistantMessageText({ children }: { children: string }) {
  return (
    <div className="space-y-2">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children: paragraph }) => <p className="whitespace-pre-wrap">{paragraph}</p>,
          ol: ({ children: items }) => <ol className="list-decimal space-y-1 pl-5">{items}</ol>,
          ul: ({ children: items }) => <ul className="list-disc space-y-1 pl-5">{items}</ul>,
          a: ({ href, children: linkText }) => (
            <a href={href} className="underline underline-offset-2" target="_blank" rel="noopener noreferrer">
              {linkText}
            </a>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

export function ChatbotPreviewPanel({
  business,
  messages,
  limits,
  messagePath,
  resetPath,
  surface,
  title,
  compact = false,
}: ChatbotPreviewPanelProps) {
  const { data, setData, post, processing, reset } = useForm({ message: "", surface });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [openListMessageId, setOpenListMessageId] = useState<number | null>(null);
  const [interactiveSubmission, setInteractiveSubmission] = useState<{
    messageId: number;
    optionId: string;
  } | null>(null);
  const busy = processing || interactiveSubmission !== null;
  const latestMessage = messages[messages.length - 1];
  const activeInteractiveMessageId =
    latestMessage?.direction === "outbound" && latestMessage.interactive ? latestMessage.id : null;
  const openListMessage = messages.find(
    (message) => message.id === openListMessageId && message.interactive?.type === "list",
  );
  const closeListPicker = useCallback(() => setOpenListMessageId(null), []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!data.message.trim() || busy || !business) return;

    post(messagePath, {
      preserveScroll: true,
      onSuccess: () => reset("message"),
    });
  };

  const selectInteractiveOption = (messageId: number, option: WhatsAppInteractiveOption) => {
    if (busy || !business || activeInteractiveMessageId !== messageId) return;

    setInteractiveSubmission({ messageId, optionId: option.id });
    router.post(
      messagePath,
      {
        interactive_reply: { message_id: messageId, option_id: option.id },
        surface,
      },
      {
        preserveScroll: true,
        onSuccess: closeListPicker,
        onFinish: () => setInteractiveSubmission(null),
      },
    );
  };

  const resetConversation = () => {
    if (!confirm("¿Reiniciar esta conversación de prueba? Las citas creadas no se eliminarán.")) return;

    router.delete(resetPath, {
      data: { surface },
      preserveScroll: true,
    });
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm" role="note">
        <p className="font-medium text-foreground">Las acciones de esta prueba son reales</p>
        <p className="mt-1 text-muted-foreground">
          No se enviarán mensajes por WhatsApp. Las citas que crees aparecerán en tu calendario;
          cancélalas cuando termines de probar.
        </p>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between gap-3 border-b border-border py-3">
          <div>
            <CardTitle className="text-base">{title || business?.name || "Prueba tu agente"}</CardTitle>
            <CardDescription>{messages.length} de {limits.stored_messages} mensajes de prueba</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={resetConversation} disabled={messages.length === 0 || busy}>
            <RotateCcw className="h-4 w-4" />
            Reiniciar
          </Button>
        </CardHeader>

        <CardContent className="p-0">
          <div
            className={cn("space-y-4 overflow-y-auto p-4", compact ? "min-h-72 max-h-[28rem]" : "min-h-[24rem] max-h-[34rem]")}
            aria-live="polite"
            aria-label="Conversación de prueba"
          >
            {messages.length === 0 ? (
              <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <Bot className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">Inicia una conversación</p>
                  <p className="mx-auto max-w-sm text-sm text-muted-foreground">
                    Escribe como cliente y prueba servicios, disponibilidad o el flujo para agendar una cita.
                  </p>
                </div>
              </div>
            ) : (
              messages.map((message) => {
                const fromUser = message.direction === "inbound";
                const interactive = message.interactive;
                const isActiveInteractive = activeInteractiveMessageId === message.id;
                return (
                  <div key={message.id} className={cn("flex items-end gap-2", fromUser && "justify-end")}>
                    {!fromUser && (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Bot className="h-4 w-4" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm sm:max-w-[70%]",
                        fromUser
                          ? "rounded-br-sm bg-primary text-primary-foreground"
                          : "rounded-bl-sm bg-muted text-foreground",
                      )}
                    >
                      {fromUser ? (
                        <p className="whitespace-pre-wrap">{message.display_message}</p>
                      ) : (
                        <AssistantMessageText>{interactive?.body || message.message}</AssistantMessageText>
                      )}

                      {interactive?.type === "list" && (
                        <div className="mt-3 border-t border-border/70 pt-2">
                          <button
                            type="button"
                            className="flex w-full items-center justify-center gap-2 rounded-md px-2 py-1.5 font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                            aria-haspopup="dialog"
                            aria-expanded={openListMessageId === message.id}
                            onClick={() => setOpenListMessageId(message.id)}
                            disabled={busy}
                          >
                            <ListChecks className="h-4 w-4" />
                            {interactive.action_label}
                          </button>
                        </div>
                      )}

                      {interactive?.type === "button" && (
                        <div className="mt-3 divide-y divide-border/70 border-t border-border/70">
                          {interactive.options.map((option) => {
                            const isSubmitting = interactiveSubmission?.messageId === message.id && interactiveSubmission.optionId === option.id;
                            return (
                              <button
                                key={option.id}
                                type="button"
                                className="flex w-full items-center justify-center gap-2 px-2 py-2 font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50"
                                onClick={() => selectInteractiveOption(message.id, option)}
                                disabled={!isActiveInteractive || busy}
                              >
                                {isSubmitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                                {option.title}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {fromUser && (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                        <UserRound className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {openListMessage?.interactive?.type === "list" && (
            <WhatsAppListPicker
              open
              interactive={openListMessage.interactive}
              active={activeInteractiveMessageId === openListMessage.id}
              submittingOptionId={
                interactiveSubmission?.messageId === openListMessage.id ? interactiveSubmission.optionId : undefined
              }
              onClose={closeListPicker}
              onSelect={(option) => selectInteractiveOption(openListMessage.id, option)}
            />
          )}

          <form onSubmit={submit} className="border-t border-border p-4">
            <div className="flex items-end gap-3">
              <Textarea
                value={data.message}
                onChange={(event) => setData("message", event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                  }
                }}
                placeholder={business ? "Escribe como si fueras un cliente…" : "Tu negocio aún no está configurado"}
                maxLength={limits.message_length}
                disabled={busy || !business}
                rows={2}
                className="max-h-40 min-h-11 resize-none"
                aria-label="Mensaje de prueba"
              />
              <Button type="submit" size="icon" disabled={busy || !business || !data.message.trim()} aria-label="Enviar mensaje de prueba">
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Enter para enviar · Shift + Enter para una nueva línea · {data.message.length}/{limits.message_length}
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
